/**
 * Cave maps — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tabletop_iii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const caveMapsPack: ToilePack = {
    slug: 'cave-maps',
    category: 'tabletop',
    name: 'Cave maps',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1216, h: 832},
    readings: [
        {key: 'small', label: 'Small cave', subject: 'a top-down cave map of four rounded chambers joined by narrow passages on a square grid, with rubble, a pool and numbered room circles'},
        {key: 'system', label: 'Cave system', subject: 'a top-down map of a large branching cave system on a square grid, an underground river running through it, a rope bridge and stalagmites'},
        {key: 'lair', label: 'Dragon lair', subject: 'a top-down map of one huge cavern on a square grid, a heap of treasure in the far end, a narrow entrance tunnel and scattered bones'},
    ],
    styles: [
        {key: 'grid', label: 'Clean grid', style: 'crisp uniform black ink line art, thin square grid, closed wall outlines, no shading', palette: 'black ink only', framing: 'on a pure white background, the full page used with a white margin'},
        {key: 'inked', label: 'Hand-inked', style: 'old-school hand-inked cartographer drawing, thick black walls, crosshatched solid rock, stippled floors, faint grid', palette: 'black ink only', framing: 'on plain white paper, the full page used with a white margin'},
        {key: 'inverse', label: 'White on black', style: 'blueprint-style white line art with thin uniform strokes and a faint square grid, closed wall outlines, no fills', palette: 'white lines only', framing: 'on a solid black background, the full page used with a black margin'},
    ],
    directions: ['frozen cave', 'lava cave', 'with traps'],
    directionHint: 'try: frozen cave',
    seo: {
        title: 'Cave maps: small cave, cave system, dragon lair',
        metaDescription: "Printable cave battle maps in three styles: a small cave of four chambers, a branching cave system and a dragon's lair cavern.",
        h1: "Cave maps: small cave, cave system, dragon's lair cavern",
        tagline: "Nine maps: a small cave, a large cave system and a dragon's lair, in three styles.",
        intro: "A cave map is drawn or printed for the party to explore, then marked up during play. This pack shows a small cave of four chambers, a large cave system with an underground river, and a dragon's lair cavern with a treasure heap. Each comes as a clean grid, a hand-inked map or white lines on black. Type a cave, such as a frozen cave or a cave with traps, and the map keeps its walls.",
        useCases: [
            'a battle map for a cave fight',
            'a map to mark up with pen during play',
            "a dragon's lair for the last encounter",
            'a handout of a cave the party is exploring',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: "Three readings in three styles: a small cave of four chambers, a large cave system with a river, and a dragon's lair cavern with a treasure heap."},
            {q: 'Which styles print in one colour?', a: 'Clean grid and hand-inked are black ink on white, so they print in black only. White on black is a blueprint-style map on a solid black background.'},
            {q: 'Can I mark up the map during play?', a: 'Yes. The clean grid version has a thin square grid and closed walls with no shading, which makes it the easiest to mark up.'},
        ],
    },
}
